import { useState, type FormEvent, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { useDashboardNavigation } from "../../hooks/useDashboardNavigation";
import { Button, Flex, Heading, Text, TextField, Box } from "@radix-ui/themes";
import { EnvelopeClosedIcon, LockClosedIcon } from "@radix-ui/react-icons";
import { LanguageSelector } from "./LanguageSelector";
import styles from "./Login.module.css";

export function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { signIn, user } = useAuth();
  const { loading: roleLoading, navigateToDashboard } =
    useDashboardNavigation();

  useEffect(() => {
    if (user && !roleLoading) {
      navigateToDashboard();
    }
  }, [user, roleLoading, navigateToDashboard]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    }
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <Flex direction="column" gap="4">
          <Box mb="2">
            <Heading size="6" mb="1">
              {t("login.welcome")}
            </Heading>
            <Text color="gray" size="2">
              {t("login.signInToContinue")}
            </Text>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("login.email")}
            </Text>
            <TextField.Root
              type="email"
              placeholder={t("login.emailPlaceholder")}
              size="3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            >
              <TextField.Slot>
                <EnvelopeClosedIcon />
              </TextField.Slot>
            </TextField.Root>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("login.password")}
            </Text>
            <TextField.Root
              type="password"
              placeholder={t("login.passwordPlaceholder")}
              size="3"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            >
              <TextField.Slot>
                <LockClosedIcon />
              </TextField.Slot>
            </TextField.Root>
          </Box>

          {error && (
            <Text color="red" size="2">
              {error}
            </Text>
          )}

          <Button type="submit" size="3" mt="2">
            {t("login.signIn")}
          </Button>

          <Text size="2" align="center" color="gray">
            <Link to="/reset-password" style={{ color: "var(--accent-11)" }}>
              {t("login.forgotPassword")}
            </Link>
          </Text>

          <Flex justify="center" mt="2">
            <LanguageSelector />
          </Flex>
        </Flex>
      </form>
    </div>
  );
}
